import { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAdminContent } from "@/context/AdminContentContext";
import { Field, Panel, TextInput } from "@/components/admin/Field";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { GalleryEditor } from "@/pages/admin/GalleryEditor";
import { DiscordGalleryPanel } from "@/pages/admin/DiscordGalleryPanel";

const IMAGE_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml,image/gif";
const VIDEO_TYPES = "video/mp4,video/webm";

function Preview({ url, video }) {
  if (!url) return <p className="text-xs text-eden-muted">Sem ficheiro definido: o site usa o texto/fundo por omissão.</p>;
  if (video) return <video src={url} muted loop autoPlay playsInline className="mt-2 h-32 rounded-sm border border-eden-border object-cover" aria-hidden="true" />;
  return <img src={url} alt="Pré-visualização" className="mt-2 h-20 rounded-sm border border-eden-border bg-eden-bg object-contain" />;
}

function UploadButton({ id, accept, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) { toast.error("Ficheiro demasiado grande (máximo 60 MB)."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success(`"${file.name}" carregado. Guarda e publica para o usar no site.`);
    } catch (err) {
      toast.error(errorMessage(err, "Não foi possível carregar o ficheiro."));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} id={`${id}-file`} type="file" accept={accept} className="sr-only" onChange={onChange} data-testid={`${id}-file-input`} />
      <label htmlFor={`${id}-file`} className={`btn-secondary h-10 cursor-pointer whitespace-nowrap ${busy ? "pointer-events-none opacity-60" : ""}`} data-testid={`${id}-upload-button`}>
        <Upload size={14} /> {busy ? "A carregar…" : "Carregar"}
      </label>
    </>
  );
}

function MediaField({ id, label, hint, value, onChange, accept, video }) {
  return (
    <Field label={label} id={id} hint={hint}>
      <div className="flex gap-2">
        <TextInput id={id} value={value} onChange={onChange} maxLength={500} placeholder="Carrega um ficheiro ou cola um caminho / URL https://" />
        <UploadButton id={id} accept={accept} onUploaded={onChange} />
      </div>
      <Preview url={value} video={video} />
    </Field>
  );
}

function MediaLibrary({ onPick }) {
  const [files, setFiles] = useState(null);
  const load = () => api.get("/admin/media").then(({ data }) => setFiles(data.files)).catch(() => setFiles([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try { await api.delete(`/admin/media/${id}`); toast.success("Ficheiro removido."); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!files) return <p className="text-sm text-eden-muted">A carregar biblioteca…</p>;
  if (!files.length) return <p className="text-sm text-eden-muted" data-testid="media-library-empty">Ainda não carregaste ficheiros. Usa os botões “Carregar” acima.</p>;
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-library">
      {files.map((f) => (
        <li key={f.id} className="flex items-center gap-3 rounded-md border border-eden-border bg-eden-bg p-3" data-testid={`media-item-${f.id}`}>
          {f.content_type.startsWith("video/") ? <video src={f.url} muted className="h-12 w-16 shrink-0 rounded-sm object-cover" /> : <img src={f.url} alt="" className="h-12 w-16 shrink-0 rounded-sm bg-eden-surface object-contain" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-eden-text" title={f.original_filename}>{f.original_filename}</p>
            <p className="text-[11px] text-eden-muted">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
            <button type="button" className="mt-1 text-[11px] text-eden-turquoise hover:underline" onClick={() => { navigator.clipboard?.writeText(f.url); toast.success("Caminho copiado."); }} data-testid={`media-copy-${f.id}`}>Copiar caminho</button>
          </div>
          <ConfirmDelete title="Remover ficheiro?" description="O ficheiro deixa de estar disponível no site. Se estiver em uso, substitui-o primeiro." onConfirm={() => remove(f.id)} testId={`media-delete-${f.id}`}>
            <button type="button" className="btn-ghost h-8 px-2 text-red-400 hover:text-red-300" aria-label="Remover ficheiro" data-testid={`media-delete-${f.id}`}><Trash2 size={14} /></button>
          </ConfirmDelete>
        </li>
      ))}
    </ul>
  );
}

export default function EditMedia() {
  const { draft, update } = useAdminContent();
  const m = draft.media;
  const set = (k) => (v) => update("media", { [k]: v });
  return (
    <>
      <Panel title="Imagens e vídeo" description="Carrega os ficheiros diretamente (até 60 MB) ou indica um caminho. Depois guarda e publica.">
        <MediaField id="media-logo" label="Logótipo (PNG/SVG)" value={m.logo_url} onChange={set("logo_url")} accept={IMAGE_TYPES} />
        <MediaField id="media-hero" label="Imagem de fundo do topo (opcional)" value={m.hero_image_url} onChange={set("hero_image_url")} accept={IMAGE_TYPES} />
        <MediaField id="media-video" label="Vídeo de fundo da loja (MP4/WebM)" hint="Reproduzido sem som e em loop, com camada escura por cima." value={m.store_video_url} onChange={set("store_video_url")} accept={VIDEO_TYPES} video />
        <MediaField id="media-poster" label="Imagem de espera do vídeo (poster)" value={m.store_poster_url} onChange={set("store_poster_url")} accept={IMAGE_TYPES} />
        <MediaField id="media-og" label="Imagem de partilha (Open Graph)" hint="1200×630 recomendado. Usada nas pré-visualizações do Discord e redes sociais." value={m.og_image_url} onChange={set("og_image_url")} accept={IMAGE_TYPES} />
      </Panel>
      <DiscordGalleryPanel />
      <GalleryEditor />
      <Panel title="Biblioteca de ficheiros" description="Todos os ficheiros carregados. Copia o caminho para o usar em qualquer campo.">
        <MediaLibrary />
      </Panel>
    </>
  );
}
